import { config } from '@iheartjane/config';
import type {
  BrandSpecial,
  MaterializedMenuProduct,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { normalizeMixpanelPropertyValues } from '@iheartjane/tracking';
import type { CartBrandDiscount, Nullable, PriceId } from '@iheartjane/types';
import compact from 'lodash-es/compact';

import { convertTimeoutToString } from '@jane/kiosk/util/lib/convertTimeoutToString/convertTimeoutToString';
import { buildFiltersList } from '@jane/kiosk/util/lib/filters/buildFiltersList';
import { getProductWeight } from '@jane/kiosk/util/lib/product/getProductWeight';
import type { CartWeightInput } from '@jane/kiosk/util/lib/product/getProductWeightInCart';
import { getProductWeightInCart } from '@jane/kiosk/util/lib/product/getProductWeightInCart';
import { getProductWeightUnit } from '@jane/kiosk/util/lib/product/getProductWeightUnit';
import { getProductWeightUnitInCart } from '@jane/kiosk/util/lib/product/getProductWeightUnitInCart';
import type { AlgoliaProduct, JaneSearchState } from '@jane/search/data-access';
import { cartSize } from '@jane/shared-ecomm/util';
import type {
  BaseStore,
  EmptyCart,
  Kiosk,
  MenuProduct,
  PendingCartProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/types';

import type { MenuCategory } from '../categories/menuCategory';
import type {
  BagInfoCart,
  CartPropertiesCart,
  DealSpecial,
} from './buildProperties';
import {
  buildBagInfoProperties,
  buildBrandSpecialProperties,
  buildCartProperties,
  buildCategoryPositionProperties,
  buildCheckoutType,
  buildDealNames,
  buildDealType,
  buildKioskSettingProperties,
  buildOrderSubmittedBrandDiscountProperties,
  buildProductProperties,
  buildSessionTimeoutProperties,
  buildStoreProperties,
  getOptionSelected,
} from './buildProperties';
import type { MixpanelEvent } from './Events';
import type { MixpanelSuperProperties, SsoChannel } from './Properties';

const track = ({ event: eventName, ...properties }: MixpanelEvent) => {
  if (config.dev || config.buildVersion === 'dev') {
    console.log('Tracking', eventName, properties);
  }

  janeMixpanel.track(eventName, normalizeMixpanelPropertyValues(properties));
};

export const register = (superProps: Partial<MixpanelSuperProperties>) => {
  janeMixpanel.register(normalizeMixpanelPropertyValues(superProps));
};

export const registerUser = (superProps: Partial<MixpanelSuperProperties>) => {
  janeMixpanel.people.set(normalizeMixpanelPropertyValues(superProps));
};

export interface TrackKioskAuthenticatedArgs {
  authDetails: {
    authType: 'authentication screen' | 'url';
    backendError: Nullable<string[]>;
    path: string;
    success: boolean;
    userError: Nullable<string[]>;
  };
  store: Store;
}

export interface TrackFirstTouchArgs {
  bizAppComms: boolean;
  categoryList: MenuCategory[];
  isJaneMode: boolean;
  kioskComms: boolean;
  onFirstTouch?: () => void;
  skippedLanding: boolean;
  specialsRowEnabled: boolean;
}

interface TrackLandingTapArgs {
  cta: string[];
  isJaneMode: boolean;
  logoDisplayed: boolean;
  store: BaseStore;
}

interface TrackOrderSubmittedArgs {
  brandSpecial?: BrandSpecial;
  cart: CartPropertiesCart | EmptyCart;
  dmType: Nullable<string[]>;
  kiosk?: Nullable<Kiosk>;
  paymentMethod: Nullable<string>;
  specials: DealSpecial[];
  store: Store;
  /** An assorted collection of params and context values useful for event tracking. */
  trackingBag: {
    birthDate?: Nullable<string>;
    checkoutError: Nullable<string>;
    message: Nullable<string>;
    /** Null if checkout is anonymous */
    phoneNumber: Nullable<string>;
  };
}
interface CheckoutInitiatedArgs {
  cart: CartPropertiesCart | EmptyCart;
  kiosk?: Nullable<Kiosk>;
  specials: DealSpecial[];
  store: Store;
}

interface SessionTimedOutSubmittedArgs {
  cart?: Nullable<BagInfoCart | EmptyCart>;
  timeoutMs: number;
}
interface TrackTimeoutDialogDisplayedArgs {
  cart?: Nullable<BagInfoCart | EmptyCart>;
  timeoutMs: number;
}
export interface AddedToBagCart extends CartWeightInput {
  id?: number;
}
interface TrackAddedProductToBagArgs {
  cart?: Nullable<AddedToBagCart>;
  cartId?: string;
  dmType: Nullable<string[]>;
  productDetails: {
    product:
      | AlgoliaProduct
      | MenuProduct
      | PendingCartProduct
      | MaterializedMenuProduct;
    productPercentageOff: Nullable<number>;
    productPriceId: PriceId;
    productQuantity?: number;
    productSavingsAmount: Nullable<number>;
    productSpecialId?: Nullable<number>;
  };
  quickAdd: boolean;
  source: string[];
  specialInScope?: StoreSpecial | V3StoreSpecial | undefined;
  store: Store;
}

interface TrackTimeoutDialogTapArgs {
  cart: Nullable<BagInfoCart | EmptyCart> | undefined;
  selectedOption: string;
  timeoutMs: number;
}

interface TrackFilteredProductListArgs {
  dealName: Nullable<string[]>;
  filteredProductCount: number;
  filterOptions: JaneSearchState<MaterializedMenuProduct>;
  source: string[];
  totalProductCount: number;
}

interface TrackSearchedTheMenuArgs {
  product?: AlgoliaProduct | MenuProduct | PendingCartProduct;
  searchTerm: string;
  selectedAutocomplete: boolean;
  store: Store;
  totalCountInMenu: number;
  totalCountInSearch: number;
}

interface TrackSortedProductListArgs {
  dealName: Nullable<string[]>;
  defaultSort: string[];
  selectedSort: string[];
  source: string[];
  totalProductCount: number;
}

interface TrackHomeMenuTapArgs {
  activeDeals: boolean;
  defaultTab: string;
  isJaneMode: boolean;
  menuName: Nullable<string>;
  optionSelected:
    | MenuCategory
    | 'bag icon'
    | 'search icon'
    | 'login icon'
    | 'logout icon'
    | 'menu switcher'
    | 'sign in prompt';
  tabPosition: Nullable<number>;
}
interface TrackProductCardTapArgs {
  dealName: Nullable<string[]>;
  dmType: Nullable<string[]>;
  potencyDisplayed: boolean;
  priceDisplayed: string;
  product:
    AlgoliaProduct | MenuProduct | PendingCartProduct | MaterializedMenuProduct;
  productPercentageOff: Nullable<number>;
  productSavingsAmount: Nullable<number>;
  ratingDisplayed: boolean;
  source: string[];
}

interface TrackDealsCardTapArgs {
  customImage: boolean;
  dealName: string[];
  dealType: string;
  defaultTab: string;
  menuName: string;
  menuOptionSelected: string;
  totalProductCount: number;
}

interface SignInArgs {
  source: string[];
  store: Store;
}

interface BankLinkingArgs extends SignInArgs {
  deviceType: string;
}

interface AbandonedSignInArgs {
  source: string[];
  /** The last step before abandoning the sign in or bank linking process */
  step: string;
  store: Store;
}

interface AbandonedBankLinkingArgs extends AbandonedSignInArgs {
  deviceType: string;
}

interface SignedInArgs {
  source: string[];
  ssoChannel: SsoChannel;
  store: Store;
}

interface CashBackDetailPageTapArgs {
  brandSpecial: BrandSpecial;
  rank: number;
  source: string[];
}

interface CashBackPromptTapArgs {
  brandSpecial: BrandSpecial;
  source: string[];
}

const calculateNumberOfFields = (kiosk?: Nullable<Kiosk>) => {
  let fields = 4;
  if (kiosk?.disable_phone_number_at_checkout) {
    fields--;
  }
  if (kiosk?.disable_birth_date_at_checkout) {
    fields--;
  }
  return fields;
};

export const trackKioskAuthenticated = ({
  authDetails: { authType, backendError, path, success, userError },
  store,
}: TrackKioskAuthenticatedArgs) =>
  track({
    event: 'Authenticated Kiosk',
    ...buildStoreProperties(store),
    'Authenticated Successfully?': success,
    'Authentication Type': [authType],
    'Backend Error Reason': success ? null : backendError,
    'Path URL': path,
    'Session ID': null,
    'User Facing Error Reason': success ? null : userError,
  });

export const trackSessionTimedOut = ({
  cart,
  timeoutMs,
}: SessionTimedOutSubmittedArgs) =>
  track({
    event: 'Session Timed Out',
    ...buildBagInfoProperties(cart),
    ...buildSessionTimeoutProperties(timeoutMs),
  });

export const trackFirstTouch = ({
  bizAppComms,
  categoryList,
  isJaneMode,
  kioskComms,
  skippedLanding,
  specialsRowEnabled,
}: TrackFirstTouchArgs) =>
  track({
    event: 'First Touch',
    ...buildCategoryPositionProperties(categoryList),
    'Communications banner enabled on kiosk?': kioskComms,
    'Communications enabled from biz app?': bizAppComms,
    'Skipped landing screen?': skippedLanding,
    'Specials row on?': specialsRowEnabled,
    'Theme Color Set?': !isJaneMode,
  });

export const trackLandingTap = ({
  cta,
  isJaneMode,
  logoDisplayed,
  store,
}: TrackLandingTapArgs) =>
  track({
    event: 'Landing Screen Tap',
    ...buildStoreProperties(store),
    'CTA on Button': cta,
    'Store Logo Displayed?': logoDisplayed,
    'Theme Color Set?': !isJaneMode,
  });

export const trackOrderSubmitted = ({
  cart,
  kiosk,
  specials,
  store,
  trackingBag: { birthDate, checkoutError, message, phoneNumber },
  dmType,
  paymentMethod,
}: TrackOrderSubmittedArgs) => {
  track({
    event: 'Order Submitted',
    ...buildCartProperties(cart),
    ...buildOrderSubmittedBrandDiscountProperties(
      (cart?.brand_discounts ?? []) as CartBrandDiscount[]
    ),
    ...buildStoreProperties(store),
    ...buildKioskSettingProperties(kiosk),
    'Birth date': birthDate ?? null,
    'Brand ID in Bag': compact(cart.products.map((p) => p.product_brand_id)),
    'Cart ID': String(cart.id),
    'Checkout Type': buildCheckoutType({
      anonymousCheckout: !phoneNumber,
      separateDeviceCheckout: kiosk?.checkout_on_separate_device,
    }),
    'Deal Name': buildDealNames({
      specialIds: cart.products.map(
        ({ special_id }) => special_id ?? undefined
      ),
      specials,
    }),
    'Deal type': buildDealType({
      specialIds: cart.products.map(
        ({ special_id }) => special_id ?? undefined
      ),
      specials,
    }),
    'Error Reason': [checkoutError ?? 'none'],
    'Order Submitted Successfully?': !checkoutError,
    'Phone Number on Order': phoneNumber,
    'Pickup notes': message,
    'QR code enabled?': Boolean(kiosk?.checkout_on_separate_device),
    'Digital Merchandising Type': dmType,
    'Payment Type': paymentMethod,
  });
};

export const trackTimeoutDialogDisplayed = ({
  cart,
  timeoutMs,
}: TrackTimeoutDialogDisplayedArgs) => {
  track({
    event: 'Timeout Dialog Displayed',
    ...buildSessionTimeoutProperties(timeoutMs),
    ...buildBagInfoProperties(cart),
  });
};

export const trackTimeoutDialogTap = ({
  selectedOption,
  cart,
  timeoutMs,
}: TrackTimeoutDialogTapArgs) => {
  track({
    event: 'Timeout Dialog Tap',
    'Option Selected': [selectedOption],
    'Timeout Settings in Internal Tools': convertTimeoutToString(timeoutMs),
    ...buildBagInfoProperties(cart),
  });
};

export const trackAddedProductToBag = ({
  cartId,
  cart,
  source,
  productDetails,
  store,
  quickAdd,
  dmType,
  specialInScope,
}: TrackAddedProductToBagArgs) => {
  const {
    product,
    productPriceId,
    productQuantity,
    productPercentageOff,
    productSavingsAmount,
  } = productDetails;

  track({
    event: 'Added Product to Bag',
    'Cart ID': cart ? (cartId ?? String(cart.id)) : null,
    Source: source,
    ...buildStoreProperties(store),
    ...buildProductProperties({
      product,
      productPercentageOff,
      productSavingsAmount,
    }),
    'Deal type': specialInScope
      ? [specialInScope.special_type.replace('_', ' ')]
      : null,
    'Quick add to bag?': quickAdd,
    Price: product.bucket_price
      ? Number(product.bucket_price)
      : Number(product[`price_${productPriceId}`]),
    Weight: getProductWeight(product.kind, productPriceId),
    'Weight Unit': getProductWeightUnit(product.kind, productPriceId),
    'Total Product Weight in Bag': getProductWeightInCart(cart),
    'Total Product Weight Unit in Bag': getProductWeightUnitInCart(cart),
    'Total Product Count in Bag': cart ? cartSize(cart) : 0,
    'Quantity Added': productQuantity ?? 1,
    'Digital Merchandising Type': dmType,
    'Deal Name': specialInScope ? [specialInScope.title] : null,
  });
};

export const trackCheckoutInitiated = ({
  cart,
  kiosk,
  specials,
  store,
}: CheckoutInitiatedArgs) => {
  track({
    event: 'Checkout Initiated',
    ...buildCartProperties(cart),
    ...buildStoreProperties(store),
    'Brand ID in Bag': compact(cart.products.map((p) => p.product_brand_id)),
    'Cart ID': String(cart.id),
    'Deal Name': buildDealNames({
      specialIds: cart.products.map(
        ({ special_id }) => special_id ?? undefined
      ),
      specials,
    }),
    'Deal type': buildDealType({
      specialIds: cart.products.map(
        ({ special_id }) => special_id ?? undefined
      ),
      specials,
    }),
    'Number of Fields': calculateNumberOfFields(kiosk),
    'Product Name in Bag': cart.products.map(({ name }) => name),
    'Pickup notes enabled?':
      buildKioskSettingProperties(kiosk)['Pickup notes enabled?'],
    'QR code enabled?': Boolean(kiosk?.checkout_on_separate_device),
  });
};

export const trackFilteredProductList = ({
  filterOptions,
  totalProductCount,
  filteredProductCount,
  source,
  dealName,
}: TrackFilteredProductListArgs) => {
  const { filtersList, filterSections } = buildFiltersList(filterOptions);
  track({
    event: 'Filtered Product List',
    'Filter Option Selected': filtersList,
    'THC % Min Selected': filterOptions.rangeFilters?.percent_thc?.min ?? null,
    'THC % Max Selected': filterOptions.rangeFilters?.percent_thc?.max ?? null,
    'CBD % Min Selected': filterOptions.rangeFilters?.percent_cbd?.min ?? null,
    'CBD % Max Selected': filterOptions.rangeFilters?.percent_cbd?.max ?? null,
    'Product Listing Type': source,
    'Total Product Count on Filtered List': filteredProductCount,
    'Total Count of Filter Options Selected': filtersList.length,
    'Total Product Count on Original List': totalProductCount,
    'Filter Sections Selected': filterSections,
    'Deal Name': dealName,
  });
};

export const trackSearchedMenuProducts = () => {
  track({
    event: 'Searched Menu Products',
  });
};

export const trackSearchedTheMenu = ({
  product,
  store,
  searchTerm,
  selectedAutocomplete,
  totalCountInSearch,
  totalCountInMenu,
}: TrackSearchedTheMenuArgs) => {
  track({
    event: 'Searched The Menu',
    'Actual Search Input': searchTerm,
    ...buildStoreProperties(store),
    'Selected Autocomplete Suggestion?': selectedAutocomplete,
    'Total Count of Products in Search Results': totalCountInSearch,
    'Total Count of Products in Menu': totalCountInMenu,
    'Product Name from Autocomplete Suggestion': product ? product.name : null,
    'Product Category from Autocomplete Suggestion': product
      ? [product.kind]
      : null,
  });
};

export const trackSortedProductList = ({
  defaultSort,
  selectedSort,
  source,
  totalProductCount,
  dealName,
}: TrackSortedProductListArgs) => {
  track({
    event: 'Sorted Product List',
    'Sort Option Selected': selectedSort,
    'Product Listing Type': source,
    'Total Product Count on List': totalProductCount,
    'Default Sort': defaultSort,
    'Deal Name': dealName,
  });
};

export const trackHomeMenuTap = ({
  activeDeals,
  defaultTab,
  menuName,
  optionSelected,
  tabPosition,
  isJaneMode,
}: TrackHomeMenuTapArgs) => {
  track({
    event: 'Home Menu Tap',
    'Active deals?': activeDeals,
    'Default tab': defaultTab,
    'Menu Name': menuName,
    'Menu Option Selected': [getOptionSelected(optionSelected)],
    'Menu Tab Position': tabPosition,
    'Theme Color Set?': !isJaneMode,
  });
};

export const trackProductCardTap = ({
  product,
  productSavingsAmount,
  productPercentageOff,
  priceDisplayed,
  ratingDisplayed,
  potencyDisplayed,
  source,
  dmType,
  dealName,
}: TrackProductCardTapArgs) => {
  track({
    event: 'Product Card Tap',
    Source: source,
    ...buildProductProperties({
      product,
      productSavingsAmount,
      productPercentageOff,
    }),
    'Brand ID': Number(product.product_brand_id),
    'Price Displayed': priceDisplayed,
    'Thumbnail Image?':
      product.image_urls && product.image_urls.length > 0 ? true : false,
    'Customer Rating displayed?': ratingDisplayed,
    'Potency Info displayed?': potencyDisplayed,
    'Digital Merchandising Type': dmType,
    'Deal Name': dealName,
  });
};

export const trackDealsCardTap = ({
  defaultTab,
  menuName,
  menuOptionSelected,
  dealType,
  customImage,
  totalProductCount,
  dealName,
}: TrackDealsCardTapArgs) => {
  track({
    event: 'Deals Card Tap',
    'Default tab': defaultTab,
    'Menu Option Selected': [menuOptionSelected],
    'Menu Name': menuName,
    'Deal type': [dealType],
    'Custom image displayed?': customImage,
    'Total Count of Products Displayed': totalProductCount,
    'Deal Name': dealName,
  });
};

export const trackSignInTap = ({ source, store }: SignInArgs) => {
  track({
    event: 'Sign In Tap',
    ...buildStoreProperties(store),
    Source: source,
  });
};

export const trackBeginsSignIn = ({ source, store }: SignInArgs) => {
  track({
    event: 'Begins Sign In',
    ...buildStoreProperties(store),
    Source: source,
  });
};

export const trackAbandonedSignIn = ({
  source,
  step,
  store,
}: AbandonedSignInArgs) => {
  track({
    event: 'Abandoned Sign In',
    ...buildStoreProperties(store),
    Source: source,
    Step: step,
  });
};

export const trackRegistered = ({
  source,
  ssoChannel,
  store,
}: SignedInArgs) => {
  track({
    event: 'Registered',
    ...buildStoreProperties(store),
    Source: source,
    'Sso Channel': ssoChannel,
  });
};

export const trackSignedIn = ({ source, ssoChannel, store }: SignedInArgs) => {
  track({
    event: 'Signed In',
    ...buildStoreProperties(store),
    Source: source,
    'Sso Channel': ssoChannel,
  });
};

export const trackSignedOut = ({
  source,
  store,
}: Pick<SignedInArgs, 'store' | 'source'>) => {
  track({
    event: 'Signed Out',
    ...buildStoreProperties(store),
    Source: source,
  });
};

export const trackLinkBankAccountTap = ({
  deviceType,
  source,
  store,
}: BankLinkingArgs) => {
  track({
    event: 'Link Bank Account Tap',
    'Device Type': deviceType,
    ...buildStoreProperties(store),
    Source: source,
  });
};

export const trackBeginsBankAccountLinking = ({
  deviceType,
  source,
  store,
}: BankLinkingArgs) => {
  track({
    event: 'Begins Bank Account Linking',
    'Device Type': deviceType,
    ...buildStoreProperties(store),
    Source: source,
  });
};

export const trackAbandonedBankAccountLinking = ({
  deviceType,
  source,
  step,
  store,
}: AbandonedBankLinkingArgs) => {
  track({
    event: 'Abandoned Bank Account Linking',
    ...buildStoreProperties(store),
    'Device Type': deviceType,
    Source: source,
    Step: step,
  });
};

export const trackLinkedBankAccount = ({
  deviceType,
  source,
  store,
}: BankLinkingArgs) => {
  track({
    event: 'Linked Bank Account',
    'Device Type': deviceType,
    ...buildStoreProperties(store),
    Source: source,
  });
};

export const trackSignInTimeout = ({
  source,
  store,
}: Pick<SignedInArgs, 'store' | 'source'>) => {
  track({
    event: 'Sign In Timeout',
    ...buildStoreProperties(store),
    Source: source,
  });
};

export const trackCashBackPromptTap = ({
  brandSpecial,
  source,
}: CashBackPromptTapArgs) => {
  track({
    event: 'Cash Back Prompt Tap',
    ...buildBrandSpecialProperties(brandSpecial),
    Source: source,
  });
};

export const trackCashBackDetailPageTap = ({
  brandSpecial,
  rank,
  source,
}: CashBackDetailPageTapArgs) => {
  track({
    event: 'Cash Back Detail Page Tap',
    ...buildBrandSpecialProperties(brandSpecial),
    'Brand Discount rank in List': rank,
    Source: source,
  });
};
